import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDialogs } from "../hooks/use-dialogs";
import usePerguntaModel from "../model/pergunta";
import useTemaModel from "../model/tema";
import { useUserStore } from "../store/user-store";
import { Pergunta } from "../types/general";
import { isUser } from "../utils/access-control";
import usePerguntaStore from "../zustand/use-pergunta-store";
import useTemaStore from "../zustand/use-tema-store";

export const ViewModelPergunta = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const temaIdParam = searchParams.get("tema");
  const { user } = useUserStore();

  const {
    perguntas,
    selectedPergunta,
    selectedTemaId,
    setSelectedPergunta,
  } = usePerguntaStore();

  const { fetchTemas, fetchMyTemas, refreshTemaStats } = useTemaModel();
  const { temas, selectedTema } = useTemaStore();

  const {
    fetchPerguntas,
    fetchPerguntasByTema,
    fetchSuggestions,
    createPergunta,
    suggestPergunta,
    updatePerguntaById,
    validatePergunta,
    rejectPergunta,
    deletePerguntaById,
  } = usePerguntaModel();

  const {
    close,
    dialog,
    showConfirmation,
    showSuccess,
    showError,
    showWarning,
  } = useDialogs();


  const [viewMode, setViewMode] = useState<"all" | "suggestions">("all");
  const [nivel, setNivel] = useState<number | undefined>(undefined);

  // Recarrega a lista (respeitando modo/nível/tema) e os contadores do tema
  const reload = (opts: {
    page?: number;
    viewMode?: "all" | "suggestions";
    nivel?: number | undefined;
  } = {}) => {
    const page = opts.page ?? perguntas.pagination.currentPage;
    const mode = opts.viewMode ?? viewMode;
    const nivelFilter = "nivel" in opts ? opts.nivel : nivel;

    if (mode === "suggestions") {
      fetchSuggestions(page, nivelFilter);
    } else if (temaIdParam) {
      fetchPerguntasByTema(Number(temaIdParam), page, nivelFilter);
    } else {
      fetchPerguntas(page, undefined, nivelFilter);
    }

    if (temaIdParam) {
      refreshTemaStats(Number(temaIdParam));
    }
  };

  const changeViewMode = (mode: "all" | "suggestions") => {
    setViewMode(mode);
    reload({ page: 1, viewMode: mode });
  };

  const changeNivel = (value: number | undefined) => {
    setNivel(value);
    reload({ page: 1, nivel: value });
  };

  useEffect(() => {
    // Para usuários USER, o parâmetro temaIdParam é OBRIGATÓRIO
    if (isUser(user) && !temaIdParam) {
      showError({
        title: "Acesso Negado",
        message: "Utilizadores USER devem acessar a página de Perguntas através de um Tema específico.",
        onConfirm: () => {
          close()
          navigate("/painel")
        }
      });
      return;
    }

    if (isUser(user)) {
      const temaCheck = temas.data.find((item) => Number(item.id) === Number(temaIdParam))
      if (!temaCheck) {
        showError({
          title: "Acesso Negado",
          message: "Não tem permissão para gerenciar este tema.",
          onConfirm: () => {
            close()
            navigate("/painel")
          }
        });
        return;
      }
    }

    // USER users should only see their assigned temas via /api/temas/my
    // ADMIN users see all temas via /api/temas
    if (temas.data.length === 0) {
      if (isUser(user)) {
        fetchMyTemas();
      } else {
        fetchTemas();
      }
    }
    reload({ page: 1, viewMode: "all", nivel: undefined });
    setViewMode("all");
    setNivel(undefined);
  }, [user, temaIdParam])

  // Select pergunta for editing
  const selectPergunta = (pergunta: Pergunta) => {
    setSelectedPergunta(pergunta);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedPergunta(undefined as any);
  };

  // Navigate back to temas
  const goToTemas = () => {
    navigate("/temas");
  };

  const handleDeletePergunta = (id: number) => {
    close();
    deletePerguntaById(id, {
      response: () => {
        showSuccess({
          title: 'Sucesso',
          message: 'Pergunta removida com sucesso!'
        });
        reload();
      },
      error: () => {
        showError({
          title: 'Erro',
          message: 'Ocorreu erro ao remover a pergunta!',
        })
      }
    })
  }

  const handleActionPergunta = (pergunta: Pergunta, action: 'add' | 'update') => {
    if (action === 'add') {
      createPergunta({
        questao: pergunta.questao,
        respostas: pergunta.respostas,
        nivel: pergunta.nivel,
        temaId: pergunta.tema_id || 0,
        author: pergunta.author
      }, {
        response: () => {
          showSuccess({
            title: 'Sucesso',
            message: 'Pergunta criada com sucesso!'
          });
          reload();
        },
        error: (error) => {
          console.log(error)
          showError({
            title: 'Erro',
            message: 'Ocorreu erro ao criar a pergunta!',
          })
        }
      });
    } else {
      updatePerguntaById(pergunta, {
        response: () => {
          showSuccess({
            title: 'Sucesso',
            message: 'Pergunta actualizada com sucesso!'
          });
          reload();
        },
        error: () => {
          showError({
            title: 'Erro',
            message: 'Ocorreu erro ao actualizar a pergunta!',
          })
        }
      });
    }
  }

  const handleValidatePergunta = (id: number) => {
    close();
    validatePergunta(id, {
      response: () => {
        showSuccess({
          title: 'Sucesso',
          message: 'Pergunta validada com sucesso!'
        });
        reload();
      },
      error: () => {
        showError({
          title: 'Erro',
          message: 'Ocorreu erro ao validar a pergunta!',
        })
      }
    })
  }

  const handleRejectPergunta = (id: number) => {
    close();
    rejectPergunta(id, {
      response: () => {
        showSuccess({
          title: 'Sucesso',
          message: 'Pergunta rejeitada com sucesso!'
        });
        reload();
      },
      error: () => {
        showError({
          title: 'Erro',
          message: 'Ocorreu erro ao rejeitar a pergunta!',
        })
      }
    })
  }

  return {
    // State
    perguntas,
    selectedPergunta,
    selectedTemaId,
    temas,
    temaIdParam,
    // Só usa os contadores se pertencerem ao tema aberto (evita snapshot de outro tema)
    tema: selectedTema && Number(selectedTema.id) === Number(temaIdParam) ? selectedTema : undefined,
    viewMode,
    nivel,
    reload,
    changeViewMode,
    changeNivel,
    // Actions
    fetchPerguntas,
    fetchPerguntasByTema,
    fetchSuggestions,
    fetchMyTemas,
    handleActionPergunta,
    handleDeletePergunta,
    handleValidatePergunta,
    handleRejectPergunta,
    selectPergunta,
    clearSelection,
    goToTemas,
    navigate,
    // Dialogs
    dialog,
    showConfirmation,
    showSuccess,
    showError,
    showWarning,
  };
};
